/**
 * Read the Scan's frozen effective_measurement_basis from persisted JSON only.
 * Never rereads live EMD, current geometry, current policy, or Current Activity.
 *
 * Layer A spatial scope may be a Zone polygon (Zone Violation) or Entire camera view (Near Miss).
 * Layer B Camera Measurement is always camera-owned entire-camera-view P/F/S.
 */

import type { ScanMeasurementSource } from '../cameraMeasurement/scanMeasurementIdentity';

export const SCAN_EFFECTIVE_MEASUREMENT_BASIS_SCHEMA = 'scan_effective_measurement_basis.v1';
export const ENTIRE_CAMERA_VIEW = 'ENTIRE_CAMERA_VIEW';
export const ZONE_POLYGON = 'ZONE_POLYGON';
export const ENTIRE_CAMERA_VIEW_LABEL = 'Entire camera view';
export const MEASUREMENT_BASIS_MISSING =
  'Measurement definition was not stored on this Scan.';

export type StoredMeasurementScopeKind = typeof ENTIRE_CAMERA_VIEW | typeof ZONE_POLYGON;

export type CanonicalStoredMeasurementBasisView = {
  source: 'stored' | 'missing';
  scopeKind: StoredMeasurementScopeKind | null;
  scopeLabel: string;
  siteId: string | null;
  areaId: string | null;
  areaLabel: string | null;
  deviceId: string | null;
  cameraId: string | null;
  cameraLabel: string | null;
  /** Layer A zone identity. Null for Entire camera view / Near Miss. */
  spatialZoneId: string | null;
  /** Layer B Camera Measurement never takes a zone polygon. */
  layerBZoneId: null;
  missingLabel: string | null;
};

function text(value?: unknown): string {
  return String(value || '').trim();
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function firstComponent(frozen: Record<string, unknown>): Record<string, unknown> {
  const components = Array.isArray(frozen.components) ? frozen.components : [];
  return asRecord(components[0]);
}

export function extractStoredEffectiveMeasurementBasis(
  container?: Record<string, unknown> | null,
): Record<string, unknown> | null {
  const payload = asRecord(container);
  const nestedReport = asRecord(payload.report);
  const sources = [
    payload,
    nestedReport,
    asRecord(payload.finalized_payload),
    asRecord(payload.report_payload),
    asRecord(payload.report_payload),
    asRecord(payload.report_content),
    asRecord(payload.scan_report),
    asRecord(nestedReport.finalized_payload),
    asRecord(nestedReport.report_payload),
    asRecord(nestedReport.report_payload),
    asRecord(nestedReport.report_content),
    asRecord(nestedReport.scan_report),
  ];
  for (const source of sources) {
    const direct = asRecord(source.effective_measurement_basis);
    const version = text(direct.schema_version || direct.schema_version);
    if (version === SCAN_EFFECTIVE_MEASUREMENT_BASIS_SCHEMA) {
      return direct;
    }
    const nested = asRecord(asRecord(source.scan_report).effective_measurement_basis);
    const nestedVersion = text(nested.schema_version || nested.schema_version);
    if (nestedVersion === SCAN_EFFECTIVE_MEASUREMENT_BASIS_SCHEMA) {
      return nested;
    }
  }
  return null;
}

export function scanFamilyFromWorkflowId(workflowId?: string | null): string {
  return text(workflowId).split('|')[0]?.toLowerCase().replace(/-/g, '_') || '';
}

export function isZoneViolationFamily(family?: string | null): boolean {
  const value = text(family).toLowerCase().replace(/-/g, '_');
  return value === 'zone_violation' || value === 'zone_intrusion';
}

function familyOf(args: {
  stored?: Record<string, unknown> | null;
  riskType?: string | null;
  workflowId?: string | null;
}): string {
  const component = firstComponent(asRecord(args.stored));
  const fromStored = text(asRecord(component.condition).condition_family);
  return (
    fromStored
    || text(args.riskType)
    || scanFamilyFromWorkflowId(args.workflowId)
  ).toLowerCase().replace(/-/g, '_');
}

export function presentStoredScanMeasurementBasis(args: {
  persisted?: Record<string, unknown> | null;
  riskType?: string | null;
  workflowId?: string | null;
}): CanonicalStoredMeasurementBasisView {
  const stored = extractStoredEffectiveMeasurementBasis(args.persisted);
  const family = familyOf({ stored, riskType: args.riskType, workflowId: args.workflowId });
  const zoneViolation = isZoneViolationFamily(family);

  if (!stored) {
    return {
      source: 'missing',
      scopeKind: zoneViolation ? ZONE_POLYGON : (family ? ENTIRE_CAMERA_VIEW : null),
      scopeLabel: zoneViolation ? 'Configured zone' : (family ? ENTIRE_CAMERA_VIEW_LABEL : ''),
      siteId: text(asRecord(args.persisted).site_id) || null,
      areaId: null,
      areaLabel: null,
      deviceId: text(asRecord(args.persisted).device_id) || null,
      cameraId: text(asRecord(args.persisted).camera_id) || null,
      cameraLabel: null,
      spatialZoneId: null,
      layerBZoneId: null,
      missingLabel: MEASUREMENT_BASIS_MISSING,
    };
  }

  const component = firstComponent(stored);
  const area = asRecord(component.operational_area || component.operational_area);
  const camera = asRecord(component.camera);
  const spatial = asRecord(component.spatial_scope || component.spatial_scope);
  const storedScopeType = text(spatial.scope_type);
  const scopeKind: StoredMeasurementScopeKind = zoneViolation || storedScopeType === ZONE_POLYGON
    ? ZONE_POLYGON
    : ENTIRE_CAMERA_VIEW;
  const storedZone = text(spatial.scope_zone_id) || (
    scopeKind === ZONE_POLYGON ? text(spatial.scope_name) : ''
  );
  return {
    source: 'stored',
    scopeKind,
    scopeLabel: scopeKind === ZONE_POLYGON
      ? (storedZone || 'Configured zone')
      : ENTIRE_CAMERA_VIEW_LABEL,
    siteId: text(stored.site_id) || text(component.site_id) || text(asRecord(args.persisted).site_id) || null,
    areaId: text(area.area_id) || null,
    areaLabel: text(area.area_name) || null,
    deviceId: text(camera.device_id) || text(asRecord(args.persisted).device_id) || null,
    cameraId: text(camera.camera_id) || text(asRecord(args.persisted).camera_id) || null,
    cameraLabel: text(camera.camera_name) || null,
    spatialZoneId: scopeKind === ZONE_POLYGON ? (storedZone || null) : null,
    layerBZoneId: null,
    missingLabel: null,
  };
}

export function resolveCanonicalLayerBMeasurementSource(args: {
  persisted?: Record<string, unknown> | null;
  riskType?: string | null;
  workflowId?: string | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  siteFallback?: string | null;
  deviceFallback?: string | null;
  cameraFallback?: string | null;
  signed?: boolean;
}): ScanMeasurementSource {
  const basis = presentStoredScanMeasurementBasis({
    persisted: args.persisted,
    riskType: args.riskType,
    workflowId: args.workflowId,
  });
  return {
    siteId: basis.siteId || text(args.siteFallback) || null,
    deviceId: basis.deviceId || text(args.deviceFallback) || null,
    cameraId: basis.cameraId || text(args.cameraFallback) || null,
    zoneId: null,
    zoneName: null,
    zoneVersion: null,
    windowStart: text(args.windowStart) || null,
    windowEnd: text(args.windowEnd) || null,
    signed: Boolean(args.signed),
  };
}
